import React from "react";
import PropTypes from "prop-types";
import PlanPrice from "./PlanPrice";

/**
 * Renders the appropriate action button/badge for a plan card.
 */
function PlanCardAction({
  isCurrent,
  isUpgrade,
  isDowngrade,
  isUpgrading,
  isOtherUpgrading,
  openingPortal,
  plan,
  onUpgradeClick,
  onManageSubscription,
  onContactSales,
  salesEmail,
}) {
  if (isCurrent) {
    return <div className="billing-plan-badge">Current plan</div>;
  }

  if (plan.price === null) {
    return (
      <button
        className="billing-plan-btn billing-plan-btn-contact"
        onClick={onContactSales}
        title={`Contact sales at ${salesEmail}`}
      >
        Contact Sales
      </button>
    );
  }

  if (isUpgrade) {
    return (
      <button
        className="billing-plan-btn billing-plan-btn-upgrade"
        onClick={() => onUpgradeClick(plan)}
        disabled={isUpgrading || isOtherUpgrading}
      >
        {isUpgrading ? "Processing..." : "Upgrade"}
      </button>
    );
  }

  if (isDowngrade) {
    return (
      <div className="billing-downgrade-wrapper">
        <button
          className="billing-plan-btn billing-plan-btn-downgrade"
          onClick={onManageSubscription}
          disabled={openingPortal}
          title="Opens Stripe billing portal where you can change or cancel your subscription"
        >
          {openingPortal ? "Opening..." : "Downgrade"}
        </button>
        <span className="billing-downgrade-hint">via Stripe Portal</span>
      </div>
    );
  }

  return null;
}

PlanCardAction.propTypes = {
  isCurrent: PropTypes.bool,
  isUpgrade: PropTypes.bool,
  isDowngrade: PropTypes.bool,
  isUpgrading: PropTypes.bool,
  isOtherUpgrading: PropTypes.bool,
  openingPortal: PropTypes.bool,
  plan: PropTypes.shape({
    price: PropTypes.number,
  }).isRequired,
  onUpgradeClick: PropTypes.func.isRequired,
  onManageSubscription: PropTypes.func.isRequired,
  onContactSales: PropTypes.func.isRequired,
  salesEmail: PropTypes.string,
};

/**
 * Individual plan card for the billing plans grid.
 * Handles different states: current, upgrade, downgrade, contact sales.
 */
const PlanCard = ({
  plan,
  isCurrent,
  isUpgrade,
  isDowngrade,
  isUpgrading,
  isOtherUpgrading,
  openingPortal,
  salesEmail,
  onUpgradeClick,
  onManageSubscription,
  toast,
}) => {
  const handleContactSales = () => {
    // Use location.href for mailto - more reliable than window.open
    const mailtoUrl = `mailto:${encodeURIComponent(salesEmail)}?subject=Enterprise%20Plan%20Inquiry`;
    window.location.href = mailtoUrl;

    // Also copy email to clipboard as a convenience
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(salesEmail)
        .then(() => {
          toast.info(`Email address copied: ${salesEmail}`);
        })
        .catch(() => {
          // Clipboard failed silently - mailto may still work
        });
    }
  };

  return (
    <div
      className={`billing-plan-card ${isCurrent ? "billing-plan-active" : ""} ${isOtherUpgrading ? "billing-plan-disabled" : ""}`}
      aria-current={isCurrent ? "true" : undefined}
      aria-disabled={isOtherUpgrading ? "true" : undefined}
    >
      <div className="billing-plan-card-header">
        <h4 className="billing-plan-card-name">{plan.name}</h4>
        <div className="billing-plan-card-price">
          <PlanPrice plan={plan} />
        </div>
      </div>
      {/* Index key — features array is static per plan (never reordered) */}
      <ul className="billing-plan-features">
        {plan.features.map((feature, idx) => (
          <li key={`${plan.id}-feature-${idx}`}>{feature}</li>
        ))}
      </ul>
      <PlanCardAction
        isCurrent={isCurrent}
        isUpgrade={isUpgrade}
        isDowngrade={isDowngrade}
        isUpgrading={isUpgrading}
        isOtherUpgrading={isOtherUpgrading}
        openingPortal={openingPortal}
        plan={plan}
        onUpgradeClick={onUpgradeClick}
        onManageSubscription={onManageSubscription}
        onContactSales={handleContactSales}
        salesEmail={salesEmail}
      />
    </div>
  );
};

PlanCard.propTypes = {
  plan: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.number,
    features: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  isCurrent: PropTypes.bool,
  isUpgrade: PropTypes.bool,
  isDowngrade: PropTypes.bool,
  isUpgrading: PropTypes.bool,
  isOtherUpgrading: PropTypes.bool,
  openingPortal: PropTypes.bool,
  salesEmail: PropTypes.string,
  onUpgradeClick: PropTypes.func.isRequired,
  onManageSubscription: PropTypes.func.isRequired,
  toast: PropTypes.shape({
    info: PropTypes.func,
  }).isRequired,
};

export default PlanCard;
